// src/store/actions/agencyActions.js
import { AGENCY_ACTIONS } from './actionTypes';
import agencyService from '../../services/agencyService';

// Fetch agencies
export const fetchAgencies = (params = {}) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.FETCH_AGENCIES_REQUEST });

    const response = await agencyService.getAgencies(params);
    
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCIES_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCIES_FAILURE,
      payload: error.message || 'Failed to fetch agencies',
    });
  }
};

// Fetch single agency
export const fetchAgency = (id) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.FETCH_AGENCY_REQUEST });

    const response = await agencyService.getAgencyById(id);
    
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCY_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCY_FAILURE,
      payload: error.message || 'Failed to fetch agency',
    });
    throw error;
  }
};

// Create agency
export const createAgency = (agencyData) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.CREATE_AGENCY_REQUEST });

    const response = await agencyService.createAgency(agencyData);
    
    dispatch({
      type: AGENCY_ACTIONS.CREATE_AGENCY_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.CREATE_AGENCY_FAILURE,
      payload: error.message || 'Failed to create agency',
    });
    throw error;
  }
};

// Update agency
export const updateAgency = (id, agencyData) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.UPDATE_AGENCY_REQUEST });

    const response = await agencyService.updateAgency(id, agencyData);
    
    dispatch({
      type: AGENCY_ACTIONS.UPDATE_AGENCY_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.UPDATE_AGENCY_FAILURE,
      payload: error.message || 'Failed to update agency',
    });
    throw error;
  }
};

// Delete agency
export const deleteAgency = (id) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.DELETE_AGENCY_REQUEST });

    const response = await agencyService.deleteAgency(id);
    
    dispatch({
      type: AGENCY_ACTIONS.DELETE_AGENCY_SUCCESS,
      payload: { id, message: response.message },
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.DELETE_AGENCY_FAILURE,
      payload: error.message || 'Failed to delete agency',
    });
    throw error;
  }
};

// Toggle agency status
export const toggleAgencyStatus = (id) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.TOGGLE_AGENCY_STATUS_REQUEST });

    const response = await agencyService.toggleAgencyStatus(id);
    
    dispatch({
      type: AGENCY_ACTIONS.TOGGLE_AGENCY_STATUS_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.TOGGLE_AGENCY_STATUS_FAILURE,
      payload: error.message || 'Failed to toggle agency status',
    });
    throw error;
  }
};

// Fetch agency services
export const fetchAgencyServices = (agencyId) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.FETCH_AGENCY_SERVICES_REQUEST });

    const response = await agencyService.getAgencyServices(agencyId);
    
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCY_SERVICES_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCY_SERVICES_FAILURE,
      payload: error.message || 'Failed to fetch agency services',
    });
  }
};

// Update agency services
export const updateAgencyServices = (agencyId, serviceIds) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.UPDATE_AGENCY_SERVICES_REQUEST });

    const response = await agencyService.updateAgencyServices(agencyId, serviceIds);
    
    dispatch({
      type: AGENCY_ACTIONS.UPDATE_AGENCY_SERVICES_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.UPDATE_AGENCY_SERVICES_FAILURE,
      payload: error.message || 'Failed to update agency services',
    });
    throw error;
  }
};

// Fetch opening hours
export const fetchOpeningHours = (agencyId) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.FETCH_OPENING_HOURS_REQUEST });

    const response = await agencyService.getOpeningHours(agencyId);
    
    dispatch({
      type: AGENCY_ACTIONS.FETCH_OPENING_HOURS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.FETCH_OPENING_HOURS_FAILURE,
      payload: error.message || 'Failed to fetch opening hours',
    });
  }
};

// Update opening hours
export const updateOpeningHours = (agencyId, hoursData) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.UPDATE_OPENING_HOURS_REQUEST });

    const response = await agencyService.updateOpeningHours(agencyId, hoursData);
    
    dispatch({
      type: AGENCY_ACTIONS.UPDATE_OPENING_HOURS_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.UPDATE_OPENING_HOURS_FAILURE,
      payload: error.message || 'Failed to update opening hours',
    });
    throw error;
  }
};

// Fetch agency documents
export const fetchAgencyDocuments = (agencyId, params = {}) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.FETCH_AGENCY_DOCUMENTS_REQUEST });

    const response = await agencyService.getAgencyDocuments(agencyId, params);
    
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCY_DOCUMENTS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.FETCH_AGENCY_DOCUMENTS_FAILURE,
      payload: error.message || 'Failed to fetch agency documents',
    });
  }
};

// Upload agency document
export const uploadAgencyDocument = (agencyId, file, documentData = {}) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.UPLOAD_AGENCY_DOCUMENT_REQUEST });

    const response = await agencyService.uploadAgencyDocument(agencyId, file, documentData);
    
    dispatch({
      type: AGENCY_ACTIONS.UPLOAD_AGENCY_DOCUMENT_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.UPLOAD_AGENCY_DOCUMENT_FAILURE,
      payload: error.message || 'Failed to upload document',
    });
    throw error;
  }
};

// Delete agency document
export const deleteAgencyDocument = (agencyId, documentId) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.DELETE_AGENCY_DOCUMENT_REQUEST });

    const response = await agencyService.deleteAgencyDocument(agencyId, documentId);
    
    dispatch({
      type: AGENCY_ACTIONS.DELETE_AGENCY_DOCUMENT_SUCCESS,
      payload: { documentId, message: response.message },
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.DELETE_AGENCY_DOCUMENT_FAILURE,
      payload: error.message || 'Failed to delete document',
    });
    throw error;
  }
};

// Bulk delete agencies
export const bulkDeleteAgencies = (ids) => async (dispatch) => {
  try {
    dispatch({ type: AGENCY_ACTIONS.BULK_DELETE_AGENCIES_REQUEST });

    const response = await agencyService.bulkDeleteAgencies(ids);
    
    dispatch({
      type: AGENCY_ACTIONS.BULK_DELETE_AGENCIES_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: AGENCY_ACTIONS.BULK_DELETE_AGENCIES_FAILURE,
      payload: error.message || 'Failed to bulk delete agencies',
    });
    throw error;
  }
};

// Set agency filters
export const setAgencyFilters = (filters) => ({
  type: AGENCY_ACTIONS.SET_AGENCY_FILTERS,
  payload: filters,
});

// Clear error
export const clearAgencyError = () => ({
  type: AGENCY_ACTIONS.CLEAR_AGENCY_ERROR,
});

// Clear success
export const clearAgencySuccess = () => ({
  type: AGENCY_ACTIONS.CLEAR_AGENCY_SUCCESS,
});

// Clear selected agency
export const clearSelectedAgency = () => ({
  type: AGENCY_ACTIONS.CLEAR_SELECTED_AGENCY,
});